import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getFeatureFlagOverrides,
  subscribeToFeatureFlagOverrides,
} from '../services/featureFlags';
import type { FeatureFlagOverride } from '../types/featureFlags';

interface FeatureFlagsContextValue {
  overrides: Record<string, FeatureFlagOverride>;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(
  undefined,
);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Record<string, FeatureFlagOverride>>(
    {},
  );
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToFeatureFlagOverrides((next) => {
      setOverrides(next);
      setLoaded(true);
    });
    return unsubscribe;
  }, []);

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({
      overrides,
      loaded,
      refresh: async () => {
        const next = await getFeatureFlagOverrides();
        setOverrides(next);
      },
    }),
    [overrides, loaded],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlagsContext(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error(
      'useFeatureFlagsContext must be used within a FeatureFlagsProvider',
    );
  }
  return ctx;
}
